/**
 * Trunnion AI - Workflow Designer Store (FLAGSHIP)
 * React Flow nodes/edges/viewport with undo/redo via zundo.
 * This is the most critical store in the application.
 */

import { create } from 'zustand';
import { temporal } from 'zundo';
import type { Node, Edge, Connection, NodeChange, EdgeChange, Viewport } from '@xyflow/react';
import { applyNodeChanges, applyEdgeChanges, addEdge } from '@xyflow/react';
import type { WorkflowNodeData, WorkflowEdgeData, ClassificationLevel } from '@/types';

interface WorkflowDesignerState {
  // Workflow metadata
  workflowId: string | null;
  workflowName: string;
  workflowDescription: string;
  workflowClassification: ClassificationLevel;
  workflowVersion: number;
  isDirty: boolean;

  // React Flow state
  nodes: Node<WorkflowNodeData>[];
  edges: Edge<WorkflowEdgeData>[];
  viewport: Viewport;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;

  // Validation
  validationErrors: string[];
  validationWarnings: string[];

  // Node/Edge operations
  setNodes: (nodes: Node<WorkflowNodeData>[]) => void;
  setEdges: (edges: Edge<WorkflowEdgeData>[]) => void;
  onNodesChange: (changes: NodeChange<Node<WorkflowNodeData>>[]) => void;
  onEdgesChange: (changes: EdgeChange<Edge<WorkflowEdgeData>>[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (node: Node<WorkflowNodeData>) => void;
  updateNodeData: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  removeNode: (nodeId: string) => void;
  removeEdge: (edgeId: string) => void;
  updateEdgeData: (edgeId: string, data: Partial<WorkflowEdgeData>) => void;

  // Selection
  setSelectedNodeId: (id: string | null) => void;
  setSelectedEdgeId: (id: string | null) => void;
  clearSelection: () => void;

  // Viewport
  setViewport: (viewport: Viewport) => void;

  // Workflow operations
  setWorkflowMeta: (meta: {
    id?: string | null;
    name?: string;
    description?: string;
    classification?: ClassificationLevel;
    version?: number;
  }) => void;
  loadWorkflow: (data: {
    id: string;
    name: string;
    description: string;
    classification: ClassificationLevel;
    version: number;
    nodes: Node<WorkflowNodeData>[];
    edges: Edge<WorkflowEdgeData>[];
    viewport?: Viewport;
  }) => void;
  resetDesigner: () => void;
  markClean: () => void;

  // Validation
  setValidation: (errors: string[], warnings: string[]) => void;
  clearValidation: () => void;
}

const initialState = {
  workflowId: null,
  workflowName: 'Untitled Workflow',
  workflowDescription: '',
  workflowClassification: 'UNCLASSIFIED' as ClassificationLevel,
  workflowVersion: 1,
  isDirty: false,
  nodes: [],
  edges: [],
  viewport: { x: 0, y: 0, zoom: 1 },
  selectedNodeId: null,
  selectedEdgeId: null,
  validationErrors: [],
  validationWarnings: [],
};

export const useWorkflowDesignerStore = create<WorkflowDesignerState>()(
  temporal(
    (set, get) => ({
      ...initialState,

      setNodes: (nodes) =>
        set({ nodes, isDirty: true }),

      setEdges: (edges) =>
        set({ edges, isDirty: true }),

      onNodesChange: (changes) =>
        set({
          nodes: applyNodeChanges(changes, get().nodes) as Node<WorkflowNodeData>[],
          isDirty: true,
        }),

      onEdgesChange: (changes) =>
        set({
          edges: applyEdgeChanges(changes, get().edges) as Edge<WorkflowEdgeData>[],
          isDirty: true,
        }),

      onConnect: (connection) =>
        set({
          edges: addEdge(
            {
              ...connection,
              type: 'dataFlow',
              animated: true,
              data: {} as WorkflowEdgeData,
            },
            get().edges,
          ) as Edge<WorkflowEdgeData>[],
          isDirty: true,
        }),

      addNode: (node) =>
        set({
          nodes: [...get().nodes, node],
          isDirty: true,
        }),

      updateNodeData: (nodeId, data) =>
        set({
          nodes: get().nodes.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, ...data } as WorkflowNodeData }
              : n,
          ),
          isDirty: true,
        }),

      removeNode: (nodeId) =>
        set({
          nodes: get().nodes.filter((n) => n.id !== nodeId),
          edges: get().edges.filter(
            (e) => e.source !== nodeId && e.target !== nodeId,
          ),
          selectedNodeId:
            get().selectedNodeId === nodeId ? null : get().selectedNodeId,
          isDirty: true,
        }),

      removeEdge: (edgeId) =>
        set({
          edges: get().edges.filter((e) => e.id !== edgeId),
          selectedEdgeId:
            get().selectedEdgeId === edgeId ? null : get().selectedEdgeId,
          isDirty: true,
        }),

      updateEdgeData: (edgeId, data) =>
        set({
          edges: get().edges.map((e) =>
            e.id === edgeId
              ? { ...e, data: { ...e.data, ...data } as WorkflowEdgeData }
              : e,
          ),
          isDirty: true,
        }),

      setSelectedNodeId: (id) =>
        set({ selectedNodeId: id, selectedEdgeId: null }),

      setSelectedEdgeId: (id) =>
        set({ selectedEdgeId: id, selectedNodeId: null }),

      clearSelection: () =>
        set({ selectedNodeId: null, selectedEdgeId: null }),

      setViewport: (viewport) =>
        set({ viewport }),

      setWorkflowMeta: (meta) =>
        set({
          ...(meta.id !== undefined && { workflowId: meta.id }),
          ...(meta.name !== undefined && { workflowName: meta.name }),
          ...(meta.description !== undefined && {
            workflowDescription: meta.description,
          }),
          ...(meta.classification !== undefined && {
            workflowClassification: meta.classification,
          }),
          ...(meta.version !== undefined && { workflowVersion: meta.version }),
          isDirty: true,
        }),

      loadWorkflow: (data) =>
        set({
          workflowId: data.id,
          workflowName: data.name,
          workflowDescription: data.description,
          workflowClassification: data.classification,
          workflowVersion: data.version,
          nodes: data.nodes,
          edges: data.edges,
          viewport: data.viewport ?? { x: 0, y: 0, zoom: 1 },
          isDirty: false,
          selectedNodeId: null,
          selectedEdgeId: null,
          validationErrors: [],
          validationWarnings: [],
        }),

      resetDesigner: () => set(initialState),

      markClean: () => set({ isDirty: false }),

      setValidation: (errors, warnings) =>
        set({ validationErrors: errors, validationWarnings: warnings }),

      clearValidation: () =>
        set({ validationErrors: [], validationWarnings: [] }),
    }),
    {
      // Zundo temporal config - track nodes/edges for undo/redo
      partialize: (state) => ({
        nodes: state.nodes,
        edges: state.edges,
      }),
      limit: 50,
    },
  ),
);
